import { useCallback, useEffect, useRef, useState } from "react";
import { useScenarioToast } from "domains/notification/hooks/useScenarioToast";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import Icon from "domains/ui/components/Icon";
import { useHandleApiError } from "infra/api/error";
import {
  GetModelsByModelIdApiResponse,
  usePutModelsByModelIdMutation,
} from "infra/api/generated/api";

import { Flex, Heading, IconButton, Input } from "@chakra-ui/react";

export default function EditableHeading({
  model,
  width = "100%",
}: {
  model: GetModelsByModelIdApiResponse["model"];
  width?: React.CSSProperties["width"];
}) {
  const { successToast } = useScenarioToast();
  const { selectedProject } = useTeamContext();
  const handleApiError = useHandleApiError();

  const inputRef = useRef<any>(null);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [modelName, setModelName] = useState<string | undefined>(undefined);

  const [updateModelTrigger, { isLoading: isUpdateModelLoading }] =
    usePutModelsByModelIdMutation();

  const updateModelName = useCallback(async () => {
    if (!model || !modelName) {
      return;
    }

    try {
      await updateModelTrigger({
        modelId: model.id,
        projectId: selectedProject.id,
        body: {
          name: modelName,
        },
      });

      successToast({
        status: "success",
        title: "Model name updated",
      });
      setEditMode(false);
    } catch (error) {
      handleApiError(error, "There was an error updating the model's name");
    }
  }, [
    model,
    successToast,
    selectedProject.id,
    updateModelTrigger,
    handleApiError,
    modelName,
  ]);

  useEffect(() => {
    if (model?.name !== undefined) {
      setModelName(model?.name);
    }
  }, [model?.name, model?.id]);

  useEffect(() => {
    if (editMode) {
      inputRef.current?.focus();
    }
  }, [editMode]);

  return (
    <Flex align={"center"} w={width}>
      {editMode && (
        <>
          <Input
            ref={inputRef}
            fontSize="md"
            fontWeight="600"
            isRequired
            onChange={(e) => {
              setModelName(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                void updateModelName();
              }
            }}
            size="sm"
            value={modelName}
          />

          <IconButton
            ml={3}
            borderRadius={20}
            aria-label="save"
            icon={<Icon id="Ui/Check" h="10px" />}
            isLoading={isUpdateModelLoading}
            onClick={updateModelName}
            size="xs"
            variant="secondary"
          />
        </>
      )}
      {!editMode && (
        <>
          <Heading size="lg">{modelName ?? "Untitled"}</Heading>
          <IconButton
            ml={3}
            borderRadius={20}
            aria-label="edit"
            icon={<Icon id="Ui/Pencil" h="12px" />}
            onClick={() => setEditMode(true)}
            size="xs"
            variant="secondary"
          />
        </>
      )}
    </Flex>
  );
}
